import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/App'; // Asumo que usas useAuth para controlar permisos
import { Plus, Search, Edit, Users, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

// Mapeo de estilos para el Badge de tipo de cliente
const typeStyles = {
    'Retail': 'bg-blue-600 text-white hover:bg-blue-600/90', // Minorista/Individual
    'Wholesale': 'bg-purple-600 text-white hover:bg-purple-600/90', // Mayorista/Empresa
    'Partner': 'bg-yellow-600 text-white hover:bg-yellow-600/90', // Socio
};

export default function ClientsPage() {
    const [clients, setClients] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth(); // Usado para determinar si el usuario tiene permisos

    // Estado para el formulario de Agregar/Editar Cliente
    const [formData, setFormData] = useState({
        name: '',
        last_name: '',
        ci: '', // Cédula de Identidad
        ruc: '', // Registro Único de Contribuyentes
        email: '',
        contact_number: '',
        client_type: 'Retail', // Valor por defecto
        allow_credit: false,
    });

    // Estado para tipos de clientes
    const [clientTypes, setClientTypes] = useState([]);
    const [isClientTypesDialogOpen, setIsClientTypesDialogOpen] = useState(false);
    const [clientTypeForm, setClientTypeForm] = useState({
        client_type: '',
        rate: 0.0
    });

    // Los usuarios "Manager" o "Admin" pueden editar/añadir
    const canEdit = user?.role === 'Admin' || user?.role === 'Manager';

    useEffect(() => {
        fetchClients();
        fetchClientTypes();
    }, []);

    const fetchClients = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${API}/clients/`);
            setClients(response.data);
        } catch (error) {
            toast.error('Error al cargar clientes');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchClientTypes = async () => {
        try {
            const response = await axios.get(`${API}/discount-rates/`);
            setClientTypes(response.data);
        } catch (error) {
            console.error('Error loading client types:', error);
            // Fallback to default types if API fails or is empty
            if (clientTypes.length === 0) {
                setClientTypes([
                    { client_type: 'Retail', rate: 0.0 },
                    { client_type: 'Wholesale', rate: 0.0 },
                    { client_type: 'Partner', rate: 0.0 }
                ]);
            }
        }
    };

    const handleClientTypeSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API}/discount-rates/`, clientTypeForm);
            toast.success('Tipo de cliente guardado');
            fetchClientTypes();
            setClientTypeForm({ client_type: '', rate: 0.0 });
        } catch (error) {
            toast.error('Error al guardar tipo de cliente');
        }
    };

    // Función de limpieza de formulario
    const resetForm = () => {
        setFormData({
            name: '',
            last_name: '',
            ci: '',
            ruc: '',
            email: '',
            contact_number: '',
            client_type: 'Retail',
            allow_credit: false,
        });
    };

    // Lógica de búsqueda y filtro
    const filteredClients = useMemo(() => {
        if (!searchQuery) return clients;
        const query = searchQuery.toLowerCase();

        return clients.filter(c =>
            c.name.toLowerCase().includes(query) ||
            c.last_name?.toLowerCase().includes(query) ||
            c.ci?.toLowerCase().includes(query) ||
            c.ruc?.toLowerCase().includes(query) ||
            c.email?.toLowerCase().includes(query)
        );
    }, [searchQuery, clients]);


    // Manejar el envío del formulario (Crear o Actualizar)
    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingClient) {
                // Actualizar Cliente
                await axios.put(`${API}/clients/${editingClient.id}`, formData);
                toast.success('Cliente actualizado exitosamente');
            } else {
                // Crear Cliente
                await axios.post(`${API}/clients/`, formData);
                toast.success('Cliente agregado exitosamente');
            }
            setIsDialogOpen(false);
            setEditingClient(null);
            resetForm();
            fetchClients(); // Recargar la lista
        } catch (error) {
            console.error('Error al guardar cliente:', error);
            toast.error(error.response?.data?.detail || 'Error al guardar el cliente.');
        }
    };

    // Cargar datos en el formulario al editar
    const handleEdit = (client) => {
        setEditingClient(client);
        setFormData({
            name: client.name || '',
            last_name: client.last_name || '',
            ci: client.ci || '',
            ruc: client.ruc || '',
            email: client.email || '',
            contact_number: client.contact_number || '',
            client_type: client.client_type || 'Retail',
            allow_credit: client.allow_credit || false,
        });
        setIsDialogOpen(true);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Cargando clientes...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold flex items-center gap-2" data-font="display">
                    <Users className="size-6 text-muted-foreground" /> Clientes
                </h1>

                <div className="flex gap-2">
                    {canEdit && (
                        <Dialog open={isDialogOpen} onOpenChange={(open) => {
                            setIsDialogOpen(open);
                            if (!open) {
                                setEditingClient(null);
                                resetForm();
                            }
                        }}>
                            <DialogTrigger asChild>
                                <Button className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]">
                                    <Plus className="mr-2 size-4" />
                                    Agregar Cliente
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle data-font="display">{editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
                                </DialogHeader>

                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="name">Nombre *</Label>
                                            <Input
                                                id="name"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="last_name">Apellido</Label>
                                            <Input
                                                id="last_name"
                                                value={formData.last_name}
                                                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="ci">Cédula (CI)</Label>
                                            <Input
                                                id="ci"
                                                value={formData.ci}
                                                onChange={(e) => setFormData({ ...formData, ci: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="ruc">RUC</Label>
                                            <Input
                                                id="ruc"
                                                value={formData.ruc}
                                                onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="email">Email *</Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="contact_number">N° Contacto</Label>
                                            <Input
                                                id="contact_number"
                                                value={formData.contact_number}
                                                onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2 sm:col-span-2">
                                            <Label htmlFor="client_type">Tipo de Cliente *</Label>
                                            <Select value={formData.client_type} onValueChange={(val) => setFormData({ ...formData, client_type: val })}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {clientTypes.map((type) => (
                                                        <SelectItem key={type.client_type} value={type.client_type}>
                                                            {type.client_type} ({(type.rate * 100).toFixed(0)}%)
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="sm:col-span-2 flex items-center space-x-2 pt-2">
                                            <Switch
                                                id="allow-credit"
                                                checked={formData.allow_credit}
                                                onCheckedChange={(checked) => setFormData({ ...formData, allow_credit: checked })}
                                            />
                                            <Label htmlFor="allow-credit" className="cursor-pointer">
                                                Permitir compras a Crédito / Cuotas
                                            </Label>
                                        </div>
                                    </div>

                                    <div className="flex justify-end gap-2 mt-6">
                                        <Button type="button" variant="ghost" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                                        <Button type="submit" className="bg-[hsl(var(--primary))] hover:bg-[#BFA774]">
                                            {editingClient ? 'Actualizar' : 'Agregar'} Cliente
                                        </Button>
                                    </div>
                                </form>
                            </DialogContent>
                        </Dialog>
                    )}

                    {canEdit && (
                        <Dialog open={isClientTypesDialogOpen} onOpenChange={setIsClientTypesDialogOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" className="ml-2">
                                    <Settings className="mr-2 size-4" />
                                    Tipos de Cliente
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Gestionar Tipos de Cliente</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                    <form onSubmit={handleClientTypeSubmit} className="flex gap-2 items-end">
                                        <div className="space-y-2 flex-1">
                                            <Label>Nombre Tipo</Label>
                                            <Input
                                                value={clientTypeForm.client_type}
                                                onChange={(e) => setClientTypeForm({ ...clientTypeForm, client_type: e.target.value })}
                                                placeholder="Ej. VIP"
                                                required
                                            />
                                        </div>
                                        <div className="space-y-2 w-24">
                                            <Label>Desc. %</Label>
                                            <Input
                                                type="number"
                                                step="0.0001"
                                                min="0"
                                                max="1"
                                                value={clientTypeForm.rate}
                                                onChange={(e) => setClientTypeForm({ ...clientTypeForm, rate: parseFloat(e.target.value) })}
                                                required
                                            />
                                        </div>
                                        <Button type="submit">Guardar</Button>
                                    </form>

                                    <div className="border rounded-md">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Tipo</TableHead>
                                                    <TableHead>Descuento</TableHead>
                                                    <TableHead></TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {clientTypes.map((type) => (
                                                    <TableRow key={type.client_type}>
                                                        <TableCell>{type.client_type}</TableCell>
                                                        <TableCell>{(type.rate * 100).toFixed(0)}%</TableCell>
                                                        <TableCell>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => setClientTypeForm({
                                                                    client_type: type.client_type,
                                                                    rate: type.rate
                                                                })}
                                                            >
                                                                <Edit className="size-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </div>

            <Card>
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Search className="size-4 text-muted-foreground" />
                        <Input
                            placeholder="Buscar por nombre, CI, RUC o email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="max-w-md"
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Nombre</TableHead>
                                    <TableHead>CI/RUC</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Email</TableHead>
                                    <TableHead>Contacto</TableHead>
                                    <TableHead>Miembro Desde</TableHead>
                                    {canEdit && <TableHead className="text-right">Acciones</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredClients.map((client) => (
                                    <TableRow key={client.id}>
                                        <TableCell className="font-medium">{client.name} {client.last_name}</TableCell>
                                        <TableCell>{client.ci || client.ruc || '-'}</TableCell>
                                        <TableCell>
                                            <Badge className={typeStyles[client.client_type] || 'secondary'}>
                                                {client.client_type}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>{client.email}</TableCell>
                                        <TableCell>{client.contact_number || '-'}</TableCell>
                                        <TableCell>{formatDate(client.created_at)}</TableCell>
                                        {canEdit && (
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleEdit(client)}
                                                >
                                                    <Edit className="size-4" />
                                                </Button>
                                                {/* Podrías añadir un botón de eliminar aquí, si es necesario */}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                {filteredClients.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                            No se encontraron clientes
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}